import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Shield, Lock, User, Eye, EyeOff, ArrowRight, AlertCircle } from "lucide-react";
import NoIndex from "@/components/NoIndex";
import { useAuth } from "@/contexts/AuthContext";

const AdminLogin = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const { login } = useAuth();

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const result = await login(email, password);
    if (!result.error) {
      navigate("/admin");
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  return (
    <><NoIndex /><div className="min-h-screen bg-[hsl(220,20%,8%)] flex items-center justify-center p-4 relative overflow-hidden">
      <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="w-full max-w-md relative z-10">
        <div className="text-center mb-8">
          <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.2, type: "spring", damping: 15 }}
            className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-primary">
            <Shield className="h-8 w-8 text-primary-foreground" />
          </motion.div>
          <h1 className="text-2xl font-extrabold text-white tracking-tight">Admin Console</h1>
          <p className="text-sm text-white/40 mt-1">Sebaco Management Portal</p>
        </div>

        <div className="bg-white/[0.04] backdrop-blur-xl border border-white/[0.08] rounded-2xl p-6 shadow-2xl">
          <form onSubmit={handleLogin} className="space-y-5">
            <div>
               <label className="text-xs font-semibold text-white/50 mb-2 block uppercase tracking-wider">Email</label>
              <div className="relative">
                <User className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-white/30" />
                 <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Enter admin email"
                  className="w-full pl-11 pr-4 py-3 rounded-xl bg-white/[0.06] text-white text-sm font-medium border border-white/[0.1] outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/30 placeholder:text-white/20 transition-all" required />
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold text-white/50 mb-2 block uppercase tracking-wider">Password</label>
              <div className="relative">
                <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-white/30" />
                <input type={showPassword ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Enter password"
                  className="w-full pl-11 pr-12 py-3 rounded-xl bg-white/[0.06] text-white text-sm font-medium border border-white/[0.1] outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/30 placeholder:text-white/20 transition-all" required />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3.5 top-1/2 -translate-y-1/2">
                  {showPassword ? <EyeOff className="h-4 w-4 text-white/30" /> : <Eye className="h-4 w-4 text-white/30" />}
                </button>
              </div>
            </div>
            {error && (
              <motion.div initial={{ opacity: 0, y: -5 }} animate={{ opacity: 1, y: 0 }}
                className="flex items-center gap-2 text-destructive text-sm bg-destructive/10 border border-destructive/20 rounded-xl px-4 py-2.5">
                <AlertCircle className="h-4 w-4 shrink-0" />
                {error}
              </motion.div>
            )}
            <motion.button type="submit" disabled={loading}
              className="flex w-full items-center justify-center gap-2 rounded-xl bg-primary py-3.5 text-sm font-bold text-primary-foreground transition-all disabled:opacity-50"
              whileTap={{ scale: 0.97 }}>
              {loading ? <div className="h-4 w-4 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" /> : <>Access Dashboard <ArrowRight className="h-4 w-4" /></>}
            </motion.button>
          </form>
        </div>
        <p className="text-center mt-6 text-xs text-white/20">Secured access · Sebaco Admin Panel</p>
      </motion.div>
    </div></>
  );
};

export default AdminLogin;
